import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	provideZoneChangeDetection
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
	NavigationError,
	provideRouter,
	withComponentInputBinding,
	withEnabledBlockingInitialNavigation,
	withNavigationErrorHandler
} from '@angular/router';
import {
	MSAL_INSTANCE,
	MsalBroadcastService,
	MsalGuard,
	MsalInterceptor,
	MsalService
} from '@azure/msal-angular';
import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';

import {
	claimChunkReload,
	isChunkLoadError,
	reloadBustingCache
} from '@demeter/lib/utils/chunk-load-error';
import { AuthService } from '@demeter/modules/auth';
import { provideAuthInit } from '@demeter/modules/auth/auth-init';

import { environment } from '../environments/environment';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(
			routes,
			withComponentInputBinding(),
			withEnabledBlockingInitialNavigation(),
			// A lazy chunk deleted by a deploy 404s during route recognition, aborting the
			// navigation — leaving the app stuck on a blank shell. Reload once to pick up the
			// current index.html and its live chunk hashes.
			withNavigationErrorHandler((e: NavigationError) => {
				if (claimChunkReload(e.error)) {
					reloadBustingCache();
					return;
				}
				// Reached only for: a chunk error with the one-per-session budget already spent
				// (a second reload would loop). Deliberately does NOT gate on the budget alone —
				// a non-chunk navigation error (e.g. a lazy module throwing during init) still
				// leaves contentState() at 'routed' with an empty outlet: navbar over blank
				// space, unresolved as of this writing. See spec §5.2/§6.
				if (isChunkLoadError(e.error)) {
					inject(AuthService).markFailed();
				}
			})
		),
		provideHttpClient(),
		importProvidersFrom([BrowserAnimationsModule]),
		...provideAuthInit(),

		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true
		},
		{
			provide: MSAL_INSTANCE,
			useValue: new PublicClientApplication({
				// MSAL Configuration
				auth: {
					clientId: environment.auth.clientId,
					authority:
						'https://login.microsoftonline.com/2c29b0af-2988-4f48-b1a6-67891642290f',
					redirectUri: environment.auth.redirectUri
				},
				cache: {
					cacheLocation: BrowserCacheLocation.LocalStorage,
					storeAuthStateInCookie: true // set to true for IE 11
				},
				system: {
					loggerOptions: {
						loggerCallback: () => {},
						piiLoggingEnabled: false
					}
				}
			})
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService
	]
};
